/**
 * Durable outbox for export-manifest recording (docs/fleet-contracts.md §4).
 *
 * Every export enqueues a record — persisted through the IndexedDB transaction
 * COMMIT (core/idb.js withStore) — before the first network attempt, so a
 * closed tab, offline export, or crashed flush never silently loses the event.
 * The client-generated eventId travels with the record; the server insert is
 * idempotent on it, which is the backstop for every race this module cannot
 * see (two tabs, a retry landing after a success, a reload mid-flight).
 *
 * Retry taxonomy (TemplateApiError.status):
 *   network / timeout (0), 429, 5xx → keep, exponential backoff, stop the pass
 *   401                             → keep (auth may return), stop the pass
 *   deduped response                → success (the same row exists)
 *   409                             → permanent (event ID belongs elsewhere)
 *   other 4xx (400/403/404/413)     → permanent: drop + reportError(status) —
 *                                     the payload can never become valid
 *
 * Capacity is BYTE-based (locks are 10–150 KB): past MAX_TOTAL_BYTES the
 * oldest records are evicted with a visible status report, never silently.
 *
 * Cross-tab: flushes coordinate through the Web Locks API when available
 * (an IndexedDB lease otherwise); server idempotency is the final backstop.
 *
 * Wall-clock use here is deliberate and fine — this is browser bookkeeping,
 * not export artifact bytes.
 */
import { createIdbHandle, withStore, promisifyRequest } from './idb.js';
import { reportError } from './report-error.js';

const DB_NAME = 'pb-fleet';
const DB_VERSION = 2;
const STORE = 'outbox';
const COORDINATION_STORE = 'coordination';
const LOCK_NAME = 'pb-fleet-outbox-flush';
const LEASE_NAME = 'flush';
const LEASE_MS = 60_000;
export const MAX_TOTAL_BYTES = 2_000_000;
const BACKOFF_BASE_MS = 30_000;
const BACKOFF_MAX_MS = 3_600_000;
const PERMANENT_STATUSES = new Set([400, 403, 404, 409, 413]);

let _handle = null;
let _idbFactory = undefined;
let _sender = null;
let _now = () => Date.now();
let _flushInFlight = null;
let _initialized = false;
// Bumped by _configureForTests so an in-flight pass from a previous
// configuration abandons itself instead of writing through the new handle.
let _epoch = 0;
let _useLocks = true;
let _activePrincipal = null;
const _leaseOwner = crypto.randomUUID();
const _stagingInProgress = new Set();

function getHandle() {
  if (!_handle) {
    _handle = createIdbHandle({
      name: DB_NAME,
      version: DB_VERSION,
      idbFactory: _idbFactory,
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: 'eventId' });
        }
        if (!db.objectStoreNames.contains(COORDINATION_STORE)) {
          db.createObjectStore(COORDINATION_STORE, { keyPath: 'name' });
        }
      }
    });
  }
  return _handle;
}

async function defaultSender(record) {
  const { recordExportManifest } = await import('../templates/auth-api.js');
  return recordExportManifest(record.projectId, {
    eventId: record.eventId,
    exportMode: record.exportMode,
    lockText: record.lockText,
    artifactHash: record.artifactHash,
    projectPublishId: record.projectPublishId ?? null,
    projectVersion: record.projectVersion ?? null,
    contentHash: record.contentHash ?? null
  });
}

function backoffMs(attempts) {
  return Math.min(BACKOFF_BASE_MS * 2 ** Math.max(attempts - 1, 0), BACKOFF_MAX_MS);
}

function classifyFailure(err) {
  const status = Number(err?.status);
  if (err?.data?.deduped === true) return 'success';
  if (!Number.isFinite(status) || status === 0 || status === 429 || status >= 500) return 'retry';
  if (status === 401) return 'retry';
  if (PERMANENT_STATUSES.has(status)) return 'permanent';
  return 'retry';
}

async function readAll() {
  return withStore(getHandle(), STORE, 'readonly', (store) =>
    promisifyRequest(store.getAll()).then((rows) => rows || [])
  );
}

function orderedByAge(records) {
  return [...records].sort(
    (a, b) => a.enqueuedAt - b.enqueuedAt || (a.eventId < b.eventId ? -1 : 1)
  );
}

/**
 * Persist a record (awaits the IDB commit), evicting oldest-first past the
 * byte cap, then kick off a flush WITHOUT awaiting the network. Returns after
 * durability, not after transmission — callers on the export path must never
 * be blocked by the fleet registry.
 * @param {{eventId:string, projectId:string, exportMode:string, lockText:string,
 *          artifactHash:string, projectPublishId?:string|null,
 *          projectVersion?:number|null, contentHash?:string|null}} input
 */
export async function stageExportEvent(input) {
  if (
    !input?.eventId ||
    !input.projectId ||
    !input.principalSub ||
    !input.lockText ||
    !input.artifactHash
  ) {
    throw new Error(
      'export-event-queue: eventId, projectId, principalSub, lockText, artifactHash are required'
    );
  }
  _stagingInProgress.add(input.eventId);
  const record = {
    eventId: input.eventId,
    projectId: input.projectId,
    principalSub: String(input.principalSub),
    exportMode: input.exportMode || 'unknown',
    lockText: input.lockText,
    artifactHash: input.artifactHash,
    projectPublishId: input.projectPublishId ?? null,
    projectVersion: input.projectVersion ?? null,
    contentHash: input.contentHash ?? null,
    bytes: new TextEncoder().encode(input.lockText).length,
    enqueuedAt: _now(),
    attempts: 0,
    nextAttemptAt: 0,
    ready: false
  };

  let existing;
  try {
    existing = await readAll();
  } catch (err) {
    _stagingInProgress.delete(input.eventId);
    throw err;
  }
  const evict = [];
  let total = existing.reduce((sum, r) => sum + (r.bytes || 0), 0) + record.bytes;
  for (const old of orderedByAge(existing)) {
    if (total <= MAX_TOTAL_BYTES) break;
    evict.push(old.eventId);
    total -= old.bytes || 0;
  }

  try {
    await withStore(getHandle(), STORE, 'readwrite', (store) => {
      for (const eventId of evict) {
        _stagingInProgress.delete(eventId);
        store.delete(eventId);
      }
      return promisifyRequest(store.put(record));
    });
  } catch (err) {
    _stagingInProgress.delete(input.eventId);
    throw err;
  }

  if (evict.length > 0) {
    reportError('Fleet outbox', new Error(`evicted ${evict.length} unrecorded export(s)`), {
      user: 'status',
      message: 'Oldest unrecorded exports were dropped to make room',
      detail: `outbox byte cap ${MAX_TOTAL_BYTES}; evicted: ${evict.join(', ')}`
    });
  }

  return { enqueued: true, evicted: evict };
}

/** Make a staged export sendable after publish/version linkage resolves. */
export async function finalizeExportEvent(eventId, linkage = {}) {
  const record = await withStore(getHandle(), STORE, 'readonly', (store) =>
    promisifyRequest(store.get(eventId))
  );
  if (!record) return { finalized: false };
  await withStore(getHandle(), STORE, 'readwrite', (store) =>
    promisifyRequest(
      store.put({
        ...record,
        projectPublishId: linkage.projectPublishId ?? null,
        projectVersion: Number.isInteger(linkage.projectVersion) ? linkage.projectVersion : null,
        contentHash: linkage.contentHash ?? record.contentHash ?? null,
        ready: true,
        nextAttemptAt: 0
      })
    )
  );
  _stagingInProgress.delete(eventId);
  void flush();
  return { finalized: true };
}

/** Compatibility helper for callers whose linkage is already known. */
export async function enqueueAndFlush(input) {
  const staged = await stageExportEvent(input);
  await finalizeExportEvent(input.eventId, input);
  return staged;
}

async function flushPass() {
  // Bind this pass to ONE handle + configuration epoch: an in-flight pass must
  // never write through a handle that was reconfigured under it (test seam
  // hygiene; in a real page the epoch never changes after boot).
  const epoch = _epoch;
  const handle = getHandle();
  const sender = _sender || defaultSender;

  let records;
  try {
    records = orderedByAge(
      await withStore(handle, STORE, 'readonly', (store) =>
        promisifyRequest(store.getAll()).then((rows) => rows || [])
      )
    );
  } catch (err) {
    reportError('Fleet outbox', err, { user: 'silent', detail: 'outbox read failed' });
    return;
  }
  const now = _now();

  for (const record of records) {
    if (epoch !== _epoch) return;
    if (record.ready === false || record.principalSub !== _activePrincipal) continue;
    if (record.nextAttemptAt > now) continue;

    let outcome = 'success';
    let failure = null;
    try {
      await sender(record);
    } catch (err) {
      outcome = classifyFailure(err);
      failure = err;
    }
    if (epoch !== _epoch) return;

    if (outcome === 'success') {
      await withStore(handle, STORE, 'readwrite', (store) =>
        promisifyRequest(store.delete(record.eventId))
      ).catch(() => {});
      continue;
    }

    if (outcome === 'permanent') {
      await withStore(handle, STORE, 'readwrite', (store) =>
        promisifyRequest(store.delete(record.eventId))
      ).catch(() => {});
      reportError('Fleet outbox', failure, {
        user: 'status',
        message: 'An export could not be recorded to the fleet registry',
        detail: `eventId=${record.eventId} status=${failure?.status} code=${failure?.data?.code || ''} — payload rejected permanently`
      });
      continue;
    }

    // retry: persist backoff state and stop the pass — the transport is down
    // or throttled, hammering the rest of the queue helps nothing.
    const attempts = (record.attempts || 0) + 1;
    await withStore(handle, STORE, 'readwrite', (store) =>
      promisifyRequest(
        store.put({ ...record, attempts, nextAttemptAt: _now() + backoffMs(attempts) })
      )
    ).catch(() => {});
    reportError('Fleet outbox', failure, {
      user: 'silent',
      detail: `retryable failure (attempt ${attempts}), backing off`
    });
    break;
  }
}

async function acquireLease() {
  return withStore(
    getHandle(),
    COORDINATION_STORE,
    'readwrite',
    (store) =>
      new Promise((resolve, reject) => {
        const get = store.get(LEASE_NAME);
        get.onerror = () => reject(get.error || new Error('outbox lease read failed'));
        get.onsuccess = () => {
          const current = get.result;
          if (current && current.owner !== _leaseOwner && current.expiresAt > _now()) {
            resolve(false);
            return;
          }
          const put = store.put({
            name: LEASE_NAME,
            owner: _leaseOwner,
            expiresAt: _now() + LEASE_MS
          });
          put.onerror = () => reject(put.error || new Error('outbox lease write failed'));
          put.onsuccess = () => resolve(true);
        };
      })
  );
}

async function releaseLease() {
  await withStore(
    getHandle(),
    COORDINATION_STORE,
    'readwrite',
    (store) =>
      new Promise((resolve, reject) => {
        const get = store.get(LEASE_NAME);
        get.onerror = () => reject(get.error || new Error('outbox lease read failed'));
        get.onsuccess = () => {
          if (get.result?.owner !== _leaseOwner) {
            resolve();
            return;
          }
          const del = store.delete(LEASE_NAME);
          del.onerror = () => reject(del.error || new Error('outbox lease delete failed'));
          del.onsuccess = () => resolve();
        };
      })
  ).catch(() => {});
}

/** Single-flight, cross-tab-coordinated flush. Never throws. */
export function flush() {
  if (_flushInFlight) return _flushInFlight;
  const run = async () => {
    // Node ≥22 also exposes navigator.locks, so tests opt OUT (_useLocks): a
    // fake-IDB pass that hangs on a closed handle would hold the web lock and
    // starve later test cases.
    const locks = _useLocks && typeof navigator !== 'undefined' ? navigator.locks : undefined;
    if (locks?.request) {
      await locks.request(LOCK_NAME, async () => {
        await flushPass();
      });
    } else {
      const acquired = await acquireLease().catch(() => false);
      if (!acquired) {
        setTimeout(() => void flush(), Math.min(LEASE_MS, 5_000));
        return;
      }
      try {
        await flushPass();
      } finally {
        await releaseLease();
      }
    }
  };
  _flushInFlight = run()
    .catch((err) => {
      reportError('Fleet outbox', err, { user: 'silent', detail: 'flush pass crashed' });
    })
    .finally(() => {
      _flushInFlight = null;
    });
  return _flushInFlight;
}

/**
 * Boot wiring: initial flush + re-flush when connectivity returns. Idempotent.
 * Called from shell.js after auth resolves (a flush before sign-in would just
 * 401-retry pointlessly).
 */
export function initExportEventQueue({ principalSub = null } = {}) {
  const nextPrincipal = principalSub ? String(principalSub) : null;
  if (nextPrincipal !== _activePrincipal) _stagingInProgress.clear();
  _activePrincipal = nextPrincipal;
  if (!_initialized) {
    _initialized = true;
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => void flush());
    }
  }
  // At account activation, any not-ready row is from a prior page lifetime:
  // the export happened but linkage did not finish. Record it honestly as
  // unlinked instead of silently losing it.
  const recovery = (async () => {
    if (!_activePrincipal) return;
    for (const record of await readAll()) {
      if (
        record.principalSub !== _activePrincipal ||
        record.ready !== false ||
        _stagingInProgress.has(record.eventId)
      ) {
        continue;
      }
      await finalizeExportEvent(record.eventId, {
        projectPublishId: null,
        projectVersion: null,
        contentHash: record.contentHash ?? null
      });
    }
    await flush();
  })().catch((err) =>
    reportError('Fleet outbox', err, { user: 'silent', detail: 'staged export recovery failed' })
  );
  return recovery;
}

/** Test seams — inject a scriptable IDB factory, sender, and clock. */
export function _configureForTests({ idbFactory, sender, now, useLocks = false } = {}) {
  _epoch += 1;
  _useLocks = useLocks;
  _idbFactory = idbFactory;
  _sender = sender || null;
  if (now) _now = now;
  if (_handle) _handle.close();
  _handle = null;
  _flushInFlight = null;
  _initialized = false;
  _activePrincipal = null;
  _stagingInProgress.clear();
}

export async function _peekOutboxForTests() {
  return orderedByAge(await readAll());
}
